import { Children, Fragment, isValidElement, type ReactNode } from 'react'
import type { FilterTypeGroups } from '@/features/legends/filters/filter-types'
import { AddFilterButton } from '@/features/legends/filters/components/AddFilterButton'
import type { FilterType } from '@/features/legends/filters/rules'
import styles from './FiltersBuilder.module.css'

export interface FiltersBuilderProps {
  /**
   * The filters, as {@link FilterCard}s on the rail.
   */
  children?: ReactNode
  isEmpty: boolean
  /**
   * Shown while there are no filters; `null` shows nothing.
   */
  emptyText?: string | null
  /**
   * Shown between filters, like "AND".
   */
  childLabel?: string
  filterTypes: FilterTypeGroups
  onAdd: (type: FilterType) => unknown
  isDisabled?: boolean
  /**
   * The add menu gets `${testId}-add`.
   */
  testId?: string
}

/**
 * A list of filters joined by a rail, with a menu to add more.
 */
export function FiltersBuilder({
  children,
  isEmpty,
  emptyText = 'No filters.',
  childLabel,
  filterTypes,
  onAdd,
  isDisabled,
  testId,
}: FiltersBuilderProps) {
  const filters = Children.toArray(children)
  return (
    <div className={styles.root} data-test={testId}>
      {isEmpty ? (
        emptyText !== null && <div className={styles.empty}>{emptyText}</div>
      ) : (
        <div className={styles.filters}>
          {filters.map((filter, i) => (
            <Fragment key={isValidElement(filter) ? filter.key : i}>
              {filter}
              {childLabel && i < filters.length - 1 && (
                <div className={styles.childLabel}>
                  <span className={styles.childLabelText}>{childLabel}</span>
                </div>
              )}
            </Fragment>
          ))}
        </div>
      )}
      <AddFilterButton
        filterTypes={filterTypes}
        onAdd={onAdd}
        isDisabled={isDisabled}
        testId={testId && `${testId}-add`}
      />
    </div>
  )
}
